import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { Bell, History, NotebookPen, Package, Pencil } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { roleCheck } from "@/lib/roleCheck";
import { userAtom } from "@/stores/authStore";
import { ActionDataBo } from "@/types/tableAction";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { DeliveryStatusBadge } from "@/components/shared/badge/deliveryStatus.badge";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { ExpeditionAttributeBadges, TrackingAlert } from "./expeditionBadges";
import { getFullName } from "./expeditionDetail.utils";

type DrawerHeaderProps = {
  expedition: GetOneExpeditionBoResponseDto;
  companyName?: string;
  isDeleted: boolean;
  onShowLogs: () => void;
  onAction: (action: ActionDataBo["action"]) => void;
};

export const DrawerHeader = ({
  expedition,
  companyName,
  isDeleted,
  onShowLogs,
  onAction,
}: DrawerHeaderProps) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const user = useAtomValue(userAtom);
  const canShowLogs = roleCheck(["ADMIN", "SUPERADMIN"], user?.role);
  const isCustomQuotationRef = !expedition.quotation;
  const ref = expedition.quotation
    ? `C-${expedition.quotation.identifier}`
    : `D-${expedition.customQuotation?.identifier ?? ""}`;

  const destination =
    expedition.deliveryInfo?.company ||
    getFullName(expedition.deliveryInfo?.firstName, expedition.deliveryInfo?.lastName) ||
    "-";

  const hasUnreadNotif = expedition.notification?.some((n) => n.readBy == null) ?? false;
  const notifBadge = toBadge(
    expedition.notification?.length ?? 0,
    hasUnreadNotif ? "yellow" : "blue",
  );
  const notesBadge = toBadge(expedition.expeditionNotes?.length ?? 0, "blue");

  return (
    <div className="border-border flex w-full items-start border-b bg-white px-5 py-3 pr-12">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-row items-center gap-6 py-2">
          <div
            className={cn(
              "flex items-center gap-2 text-sm text-gray-500",
              isCustomQuotationRef && "text-orange-500",
            )}
          >
            <Package className="size-5 shrink-0" />
            <span className="truncate">{ref}</span>
          </div>
          <p className="truncate text-lg font-semibold text-gray-900">
            {companyName ? `${companyName} → ${destination}` : destination}
          </p>
        </div>
        <div className="flex flex-wrap justify-between">
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <div className="flex items-center gap-1.5">
              <DeliveryStatusBadge type={expedition.deliveryStatus as DeliveryStatusEnum} />
            </div>
            <TrackingAlert expedition={expedition} />
            <ExpeditionAttributeBadges expedition={expedition} />
          </div>
          <div className="flex items-center gap-2 pt-1">
            {!isDeleted && (
              <HeaderActionButton
                label={t("expeditionDrawer.modify")}
                icon={Pencil}
                onClick={() => onAction("MODIFY")}
              />
            )}
            <HeaderActionButton
              label={t("expeditionDrawer.notes")}
              icon={NotebookPen}
              onClick={() => onAction("NOTES")}
              badge={notesBadge}
            />
            <HeaderActionButton
              label={t("expeditionDrawer.notifications")}
              icon={Bell}
              onClick={() => onAction("NOTIFICATION")}
              badge={notifBadge}
            />
            {canShowLogs && (
              <HeaderActionButton
                label={t("expeditionDrawer.history")}
                icon={History}
                onClick={onShowLogs}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type HeaderActionButtonProps = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  badge?: { count: number; variant: "blue" | "yellow" };
};

const toBadge = (count: number, variant: "blue" | "yellow"): HeaderActionButtonProps["badge"] =>
  count > 0 ? { count, variant } : undefined;

const HeaderActionButton = ({ label, icon: Icon, onClick, badge }: HeaderActionButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        onClick={onClick}
        className="border-border relative flex size-8 shrink-0 items-center justify-center rounded-lg border bg-white text-gray-900 shadow-xs transition-colors hover:bg-gray-50"
      >
        <Icon className="size-4" />
        {badge && badge.count > 0 && (
          <span
            className={cn(
              "absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white",
              badge.variant === "yellow" ? "bg-yellow-400" : "bg-blue-500",
            )}
          >
            {badge.count}
          </span>
        )}
      </button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);
